/**
 * profileCompleteness — pure function for determining if a HealthProfile
 * is "complete enough" to dismiss the login onboarding modals.
 *
 * This is the FRONTEND/onboarding gate (ADR-2). It is separate from the
 * engine's isProfileComplete (which checks only the 6 motor fields for D15).
 *
 * Required fields for onboarding completeness:
 *   sex, ageYears (or birthDate), weightKg, heightCm, activityLevel,
 *   objective, dietType
 *
 * Clinical fields (allergies, conditions, etc.) are NOT required.
 * Having profileSource="structured" + dietType is the signal the user
 * has visited and saved the clinical section (ADR-2).
 *
 * Returns { complete: boolean, missing: string[] }.
 * Missing lists the display names of absent required fields.
 */

import type { HealthProfile } from "@autodieta/shared";

export interface ProfileCompletenessResult {
  complete: boolean;
  /** Display names of missing required fields */
  missing: string[];
}

/**
 * Determine if the profile satisfies the onboarding completion gate.
 *
 * @param profile - Partial HealthProfile (may be freshly fetched, incomplete)
 * @returns { complete, missing }
 */
export function isProfileComplete(
  profile: Partial<HealthProfile>,
): ProfileCompletenessResult {
  const missing: string[] = [];

  // Required: sex
  if (!profile.sex) {
    missing.push("sex");
  }

  // Required: ageYears OR derivable from birthDate
  const hasAge =
    profile.ageYears !== undefined && profile.ageYears > 0;
  const hasBirthDate =
    typeof profile.birthDate === "string" && profile.birthDate.length > 0;
  if (!hasAge && !hasBirthDate) {
    missing.push("ageYears");
  }

  // Required: weightKg
  if (profile.weightKg === undefined || profile.weightKg <= 0) {
    missing.push("weightKg");
  }

  // Required: heightCm
  if (profile.heightCm === undefined || profile.heightCm <= 0) {
    missing.push("heightCm");
  }

  // Required: activityLevel
  if (!profile.activityLevel) {
    missing.push("activityLevel");
  }

  // Required: objective
  if (!profile.objective) {
    missing.push("objective");
  }

  // Required: dietType (signals user has visited dietary pattern section)
  if (!profile.dietType) {
    missing.push("dietType");
  }

  return {
    complete: missing.length === 0,
    missing,
  };
}
